import ReconnectingWebSocket from 'reconnecting-websocket';

import { proxyAxios } from 'api/apiBase/axios';
import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { cexExchangesTimeframes, IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';
import { ECexName } from 'web3';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { aggregateOrderbook, mergeOrderBook, sortOrderbookFunc } from '../utils';

export class WhitebitStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  pingInterval: ReturnType<typeof setInterval> | undefined;

  bids: [string, string][];
  asks: [string, string][];
  pair: string;
  depth: number;
  precision: number;
  onOrderbookUpdate: (v: IOrderbook) => void;
  onTickerUpdate: (v: ITickerSocket) => void;
  klineUpdate: (v: KLine) => void;
  precisionUpdate: (v: number) => void;
  onTradeUpdate: (v: TradeSocket) => void;

  selectedPrecision: number;

  depthId: number;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    klineUpdate,
    precisionUpdate,
    onTradeUpdate,
  }: IInitArgs) {
    this.pair = `${base.toUpperCase()}_${quote.toUpperCase()}`;
    this.bids = [];
    this.asks = [];
    this.depth = orderbookDepth ?? 20;
    this.precision = 1;
    this.selectedPrecision = 1;
    this.depthId = 100;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
    this.onTradeUpdate = onTradeUpdate;
  }

  fetchTickerPrecision = async () => {
    try {
      const url = new URL('https://whitebit.com/api/v2/public/markets');

      const res = await proxyAxios.get<{
        result: {
          name: string;
          moneyPrec: number;
        }[];
      }>('/api/v1/proxy', { params: { url: url.toString() } });

      let prec = 1;

      if (res.data) {
        const symbol = res.data.result.find(el => el.name === this.pair);

        if (symbol) {
          prec = symbol.moneyPrec;
        }
      }

      this.precision = prec;
      this.selectedPrecision = prec;
      this.precisionUpdate(prec);
    } catch (error) {
      console.log('Whitebit ticker error: ', error);
    }
  };

  fetchKlines = async ({
    base,
    quote,
    limit,
    from: _from,
    to: _to,
    interval,
  }: {
    base: string;
    quote: string;
    from: number;
    to: number;
    limit: number;
    interval: IChartTimeframe;
  }) => {
    const innerInterval = cexExchangesTimeframes[interval][ECexName.whitebit];

    try {
      const url = new URL('https://whitebit.com/api/v1/public/kline');
      const params = new URLSearchParams({
        market: `${base.toUpperCase()}_${quote.toUpperCase()}`,
        limit: limit.toString(),
        interval: innerInterval,
      });

      url.search = params.toString();

      const result = await proxyAxios.get<{ result: (string | number)[][] }>('/api/v1/proxy', {
        params: {
          url: url.toString(),
        },
      });

      return (result.data.result ?? []).map(el => [
        Number(el[0]) * 1000,
        el[1],
        el[3],
        el[4],
        el[2],
        el[5],
      ]) as KLine[];
    } catch (error) {
      console.log('Whitebit klines error: ', error);
      return [];
    }
  };

  fetchTrades = async ({ base, quote, limit }: { base: string; quote: string; limit: number }) => {
    try {
      const url = new URL('https://whitebit.com/api/v1/public/history');
      const params = new URLSearchParams({
        market: `${base.toUpperCase()}_${quote.toUpperCase()}`,
        limit: limit.toString(),
        lastId: '1',
      });

      url.search = params.toString();

      const result = await proxyAxios.get<{
        result: {
          time: string;
          price: string;
          amount: string;
          type: string;
        }[];
      }>('/api/v1/proxy', { params: { url: url.toString() } });

      return (
        (result.data.result ?? []).map(item => ({
          price: item.price,
          quantity: item.amount,
          timestamp: item.time,
          side: item.type,
        })) ?? []
      );
    } catch (error) {
      console.log('Whitebit trades error: ', error);
      return [];
    }
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    if (this.ws) {
      this.closeWebSocket();
    }

    const ws = new ReconnectingWebSocket('wss://api.whitebit.com/ws', [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    this.ws = ws;

    const intervalChannel = {
      60000: 60,
      300000: 300,
      900000: 900,
      3600000: 3600,
      14400000: 14400,
      86400000: 86400,
      604800000: 604800,
    }[interval];

    const marketId = 11;
    const klinesId = 12;
    const pingId = 13;
    const tradesId = 8;

    this.pingInterval = setInterval(() => {
      if (this.ws) {
        this.ws.send(
          JSON.stringify({
            id: pingId,
            method: 'ping',
            params: [],
          }),
        );
      }
    }, 40 * 1000);

    this.ws.onopen = () => {
      ws.send(
        JSON.stringify({
          id: this.depthId,
          method: 'depth_subscribe',
          params: [this.pair, this.depth, normalizeNumber(1 / 10 ** this.selectedPrecision)],
        }),
      );

      ws.send(
        JSON.stringify({
          id: marketId,
          method: 'market_subscribe',
          params: [this.pair],
        }),
      );

      if (intervalChannel) {
        ws.send(
          JSON.stringify({
            id: klinesId,
            method: 'candles_subscribe',
            params: [this.pair, intervalChannel],
          }),
        );
      }

      ws.send(
        JSON.stringify({
          id: tradesId,
          method: 'trades_subscribe',
          params: [this.pair],
        }),
      );
    };

    this.ws.onmessage = event => {
      try {
        const data = JSON.parse(event.data);

        if (data.method === 'depth_update') {
          const action = data.params[0] === true ? 'snapshot' : 'update';

          const responseAsks = (data.params[1].asks ?? []) as [string, string][];
          const responseBids = (data.params[1].bids ?? []) as [string, string][];

          const asks =
            action === 'snapshot'
              ? mergeOrderBook(responseAsks, []).sort(sortOrderbookFunc)
              : mergeOrderBook(this.asks, responseAsks).sort(sortOrderbookFunc);

          const bids =
            action === 'snapshot'
              ? mergeOrderBook(responseBids, []).sort(sortOrderbookFunc)
              : mergeOrderBook(this.bids, responseBids).sort(sortOrderbookFunc);

          this.asks = asks;
          this.bids = bids;

          this.onOrderbookUpdate(
            aggregateOrderbook(
              asks.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
              bids.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
              this.selectedPrecision,
            ),
          );
        }

        if (data.method === 'market_update') {
          const { open, low, high, last, volume, deal } = data.params[1];

          const change = normalizeNumber(((Number(last) - Number(open)) / Number(open)) * 100);

          this.onTickerUpdate({
            baseVolume: volume,
            change: change,
            close: last,
            high: high,
            last: Number(last).toFixed(this.precision),
            low: low,
            quoteVolume: deal,
          });
        }

        if (data.method === 'candles_update') {
          const [time, open, close, high, low, volume] = data.params[0];

          this.klineUpdate([time, open, high, low, close, volume]);
        }

        if (data.method === 'trades_update') {
          if (data.params[1].length === 1) {
            const { amount, price, time, type } = data.params[1][0];

            this.onTradeUpdate({
              price,
              quantity: amount,
              timestamp: time,
              side: type,
            });
          }
        }
      } catch (error) {
        console.log('Whitebit socket error', error);
      }
    };
  };

  closeWebSocket = () => {
    if (this.ws) {
      this.ws.close();
    }
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }
  };

  setOrderbookDepth(depth: number) {
    if (this.ws) {
      this.ws.send(
        JSON.stringify({
          id: this.depthId,
          method: 'depth_unsubscribe',
          params: [this.pair, this.depth, normalizeNumber(1 / 10 ** this.selectedPrecision)],
        }),
      );
    }

    this.depth = depth;

    if (this.ws) {
      this.ws.send(
        JSON.stringify({
          id: this.depthId,
          method: 'depth_subscribe',
          params: [this.pair, this.depth, normalizeNumber(1 / 10 ** this.selectedPrecision)],
        }),
      );
    }
  }

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    if (this.ws) {
      this.ws.send(
        JSON.stringify({
          id: this.depthId,
          method: 'depth_unsubscribe',
          params: [this.pair, this.depth, normalizeNumber(1 / 10 ** this.selectedPrecision)],
        }),
      );

      this.selectedPrecision = tick;

      this.depthId += 1;

      this.ws.send(
        JSON.stringify({
          id: this.depthId,
          method: 'depth_subscribe',
          params: [this.pair, this.depth, normalizeNumber(1 / 10 ** this.selectedPrecision)],
        }),
      );
    }
  };

  getOrderbookTicks = (prec: number) =>
    Array.from({ length: prec })
      .fill(0)
      .map((_, idx) => idx + 1)
      .map(el => ({ value: normalizeNumber(1 / 10 ** el), tick: el, id: el }))
      .slice(-3);

  fetch = async () => {
    await this.fetchTickerPrecision();
  };

  static init = async (args: IInitArgs) => {
    const instance = new WhitebitStrategy(args);

    await instance.fetch();

    return instance;
  };
}
